<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, CustomCheckbox, FormInput, ThemeProvider, UiModalHeader } from '@/shared/ui';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import type { SettingsListItem } from '../model/settings.types';

  const props = withDefaults(
    defineProps<{
      mode: 'add' | 'edit';
      title: string;
      initialName?: string;
      initialRecordTypeIds?: readonly number[];
      initialProtectionMeanIds?: readonly number[];
      recordTypeOptions: readonly SettingsListItem[];
      protectionMeanOptions: readonly SettingsListItem[];
      isSaving?: boolean;
      hasNameConflict?: boolean;
      validationMessages?: readonly string[];
    }>(),
    {
      initialName: '',
      initialRecordTypeIds: () => [],
      initialProtectionMeanIds: () => [],
      isSaving: false,
      hasNameConflict: false,
      validationMessages: () => [],
    }
  );

  const emit = defineEmits<{
    close: [];
    save: [payload: { name: string; recordTypeIds: number[]; protectionMeanIds: number[] }];
  }>();

  const NAME_MAX_LENGTH = 100;

  const name = ref(props.initialName);
  const selectedRecordTypeIds = ref<Set<number>>(new Set(props.initialRecordTypeIds));
  const selectedProtectionMeanIds = ref<Set<number>>(new Set(props.initialProtectionMeanIds));
  const isRejectionDismissed = ref(false);

  const visibleValidationMessages = computed(() =>
    isRejectionDismissed.value ? [] : props.validationMessages
  );

  /**
   * The server decides what is the same name, so there is no local pre-check here: a second opinion
   * would mean two rules on one screen. Editing the name drops the highlight until the next save.
   *
   * The refused message lands on the name field, and not below the form, because the name is the
   * only thing the server can refuse by validation: Salvar stays disabled until both link sets
   * carry an option that still exists, so the two arrays never leave the screen empty or invalid.
   */
  const nameError = computed(() => {
    if (isRejectionDismissed.value) return undefined;

    return props.hasNameConflict ? 'Nome duplicado' : visibleValidationMessages.value[0];
  });

  /**
   * Validation runs on the identifiers that still exist among the options, and not on the ones the
   * form was seeded with. A link whose row was deleted is dropped on the way out, so counting it
   * here would enable Salvar over a selection the save then throws away, taking the links of an
   * origin nobody touched.
   */
  const effectiveRecordTypeIds = computed(() =>
    props.recordTypeOptions.filter((option) => selectedRecordTypeIds.value.has(option.id))
  );
  const effectiveProtectionMeanIds = computed(() =>
    props.protectionMeanOptions.filter((option) => selectedProtectionMeanIds.value.has(option.id))
  );

  const isFormValid = computed(
    () =>
      name.value.trim().length > 0 &&
      effectiveRecordTypeIds.value.length > 0 &&
      effectiveProtectionMeanIds.value.length > 0
  );

  const updateName = (value: string) => {
    name.value = value;
    isRejectionDismissed.value = true;
  };

  const toggleRecordType = (id: number) => {
    if (selectedRecordTypeIds.value.has(id)) selectedRecordTypeIds.value.delete(id);
    else selectedRecordTypeIds.value.add(id);
  };

  const toggleProtectionMean = (id: number) => {
    if (selectedProtectionMeanIds.value.has(id)) selectedProtectionMeanIds.value.delete(id);
    else selectedProtectionMeanIds.value.add(id);
  };

  const handleClose = () => emit('close');

  const handleSave = () => {
    if (props.isSaving) return;

    isRejectionDismissed.value = false;
    emit('save', {
      name: name.value.trim(),
      recordTypeIds: effectiveRecordTypeIds.value.map((option) => option.id),
      protectionMeanIds: effectiveProtectionMeanIds.value.map((option) => option.id),
    });
  };
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] max-h-[90vh] shadow-lg overflow-hidden"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider class="flex flex-col flex-1 min-h-0 overflow-hidden">
      <UiModalHeader
        class="px-6 py-4 text-sm border-b border-gray-02 shrink-0"
        :title="title"
        @close="handleClose"
      />

      <div class="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-6">
        <FormInput
          id="record-origin-name"
          :model-value="name"
          label="Origem do Registro:"
          type="text"
          placeholder="Informe o nome da Origem"
          :max-length="NAME_MAX_LENGTH"
          label-class="text-sm text-gray-07 font-semibold"
          :error="nameError"
          @update:model-value="updateName"
        />

        <hr class="border-t border-gray-02 -mx-6">

        <div class="flex flex-col gap-2">
          <label class="text-sm text-gray-07 font-semibold">Tipos de Registro:</label>
          <span class="text-xs text-gray-05">
            Selecione os tipos que estarão vinculados à Origem do Registro
          </span>

          <div class="border rounded-lg max-h-48 overflow-y-auto flex flex-col">
            <div
              v-for="option in recordTypeOptions"
              :key="option.id"
              class="flex items-center py-2 px-4 hover:bg-gray-50 cursor-pointer"
              @click="toggleRecordType(option.id)"
            >
              <CustomCheckbox
                :id="`record-type-${option.id}`"
                :checked="selectedRecordTypeIds.has(option.id)"
              />
              <span class="ml-3 text-sm text-gray-07 select-none">{{ option.name }}</span>
            </div>
            <div
              v-if="recordTypeOptions.length === 0"
              class="flex justify-center items-center py-4 text-gray-05 text-sm"
            >
              Nenhum tipo de registro cadastrado
            </div>
          </div>
        </div>

        <hr class="border-t border-gray-02 -mx-6">

        <div class="flex flex-col gap-2">
          <label class="text-sm text-gray-07 font-semibold">Meios de Proteção:</label>

          <div class="border rounded-lg max-h-48 overflow-y-auto flex flex-col">
            <div
              v-for="option in protectionMeanOptions"
              :key="option.id"
              class="flex items-center py-2 px-4 hover:bg-gray-50 cursor-pointer"
              @click="toggleProtectionMean(option.id)"
            >
              <CustomCheckbox
                :id="`protection-mean-${option.id}`"
                :checked="selectedProtectionMeanIds.has(option.id)"
              />
              <span class="ml-3 text-sm text-gray-07 select-none">{{ option.name }}</span>
            </div>
            <div
              v-if="protectionMeanOptions.length === 0"
              class="flex justify-center items-center py-4 text-gray-05 text-sm"
            >
              Nenhum meio de proteção cadastrado
            </div>
          </div>
        </div>
      </div>

      <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 shrink-0">
        <ColoredButton
          text="Cancelar"
          outlined
          color="gray-03"
          :disabled="isSaving"
          @click="handleClose"
        />
        <ColoredButton
          text="Salvar"
          :disabled="!isFormValid || isSaving"
          :loading="isSaving"
          :icon="CheckMarkIcon"
          @click="handleSave"
        />
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
